/**
 * Input Sanitization Service
 * Comprehensive XSS protection using DOMPurify with custom configurations
 */

import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify';

/**
 * Sanitization configuration profiles
 */
export enum SanitizationProfile {
  /** Strict - No HTML allowed, plain text only */
  STRICT = 'strict',
  /** Basic - Allow basic formatting tags only */
  BASIC = 'basic',
  /** Standard - Allow common safe HTML tags */
  STANDARD = 'standard',
  /** Rich - Allow rich HTML with strict attribute filtering */
  RICH = 'rich',
}

/**
 * Sanitization options
 */
export interface SanitizationOptions {
  /** Sanitization profile */
  profile?: SanitizationProfile;

  /** Additional allowed tags */
  allowedTags?: string[];

  /** Additional allowed attributes */
  allowedAttributes?: string[];

  /** Allow data attributes */
  allowDataAttributes?: boolean;

  /** Return DOM node instead of string */
  returnDOM?: boolean;
}

/**
 * Profile configurations for DOMPurify
 */
const PROFILE_CONFIGS: Record<SanitizationProfile, DOMPurifyConfig> = {
  [SanitizationProfile.STRICT]: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  },

  [SanitizationProfile.BASIC]: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p'],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  },

  [SanitizationProfile.STANDARD]: {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'b', 'i', 'u',
      'ul', 'ol', 'li', 'a', 'span', 'div',
    ],
    ALLOWED_ATTR: ['href', 'title', 'target'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  },

  [SanitizationProfile.RICH]: {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's',
      'ul', 'ol', 'li', 'a', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'blockquote', 'pre', 'code',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
    ],
    ALLOWED_ATTR: ['href', 'title', 'target', 'class', 'id'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  },
};

/**
 * Sanitization Service using DOMPurify
 */
export class SanitizationService {
  private purify: typeof DOMPurify;

  constructor() {
    this.purify = DOMPurify;
    this.configureDOMPurify();
  }

  /**
   * Configure DOMPurify with security hooks
   */
  private configureDOMPurify(): void {
    // Add hook to sanitize URL schemes
    this.purify.addHook('afterSanitizeAttributes', (node: Element) => {
      // Ensure links use safe protocols
      if (node.hasAttribute('href')) {
        const href = node.getAttribute('href') || '';
        if (!this.isSafeURL(href)) {
          node.removeAttribute('href');
        }
      }

      // Force external links to open in new window with noopener
      if (node.tagName === 'A' && node.hasAttribute('href')) {
        const href = node.getAttribute('href') || '';
        if (href.startsWith('http')) {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      }
    });
  }

  /**
   * Check if URL uses a safe protocol
   */
  private isSafeURL(url: string): boolean {
    const safeProtocols = ['http:', 'https:', 'mailto:', 'tel:'];

    try {
      const urlObj = new URL(url, window.location.origin);
      return safeProtocols.includes(urlObj.protocol);
    } catch {
      // Relative URLs are safe
      return !url.includes(':');
    }
  }

  /**
   * Sanitize HTML string
   */
  public sanitizeHTML(
    dirty: string,
    options: SanitizationOptions = {}
  ): string | Node {
    const profile = options.profile || SanitizationProfile.STANDARD;
    const config = { ...PROFILE_CONFIGS[profile] };

    // Merge additional allowed tags
    if (options.allowedTags) {
      config.ALLOWED_TAGS = [
        ...(config.ALLOWED_TAGS || []),
        ...options.allowedTags,
      ];
    }

    // Merge additional allowed attributes
    if (options.allowedAttributes) {
      config.ALLOWED_ATTR = [
        ...(config.ALLOWED_ATTR || []),
        ...options.allowedAttributes,
      ];
    }

    // Allow data attributes if specified
    if (options.allowDataAttributes) {
      config.ALLOW_DATA_ATTR = true;
    }

    // Return DOM node if requested
    if (options.returnDOM) {
      config.RETURN_DOM = true;
    }

    return this.purify.sanitize(dirty, config);
  }

  /**
   * Sanitize text input (strips all HTML)
   */
  public sanitizeText(input: string): string {
    return this.sanitizeHTML(input, {
      profile: SanitizationProfile.STRICT,
    }) as string;
  }

  /**
   * Sanitize user-generated content with basic formatting
   */
  public sanitizeUserContent(input: string): string {
    return this.sanitizeHTML(input, {
      profile: SanitizationProfile.BASIC,
    }) as string;
  }

  /**
   * Sanitize email address
   */
  public sanitizeEmail(email: string): string {
    const sanitized = this.sanitizeText(email).trim().toLowerCase();

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitized)) {
      return '';
    }

    return sanitized;
  }

  /**
   * Sanitize phone number
   */
  public sanitizePhone(phone: string): string {
    // Remove all non-digit characters
    const digits = this.sanitizeText(phone).replace(/\D/g, '');

    // Validate length (10-15 digits is reasonable)
    if (digits.length < 10 || digits.length > 15) {
      return '';
    }

    return digits;
  }

  /**
   * Sanitize URL
   */
  public sanitizeURL(url: string): string {
    const sanitized = this.sanitizeText(url).trim();

    if (!this.isSafeURL(sanitized)) {
      return '';
    }

    try {
      const urlObj = new URL(sanitized);
      return urlObj.toString();
    } catch {
      return '';
    }
  }

  /**
   * Sanitize object (recursively sanitize all string values)
   */
  public sanitizeObject<T extends Record<string, any>>(
    obj: T,
    profile: SanitizationProfile = SanitizationProfile.STRICT
  ): T {
    const sanitized: any = {};

    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        sanitized[key] = this.sanitizeHTML(value, { profile });
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map((item) =>
          typeof item === 'string'
            ? this.sanitizeHTML(item, { profile })
            : item
        );
      } else if (value !== null && typeof value === 'object') {
        sanitized[key] = this.sanitizeObject(value, profile);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized as T;
  }

  /**
   * Validate and sanitize date string
   */
  public sanitizeDate(dateString: string): string {
    const sanitized = this.sanitizeText(dateString);

    // Try parsing as date
    const date = new Date(sanitized);
    if (isNaN(date.getTime())) {
      return '';
    }

    // Return ISO format
    return date.toISOString().split('T')[0];
  }

  /**
   * Sanitize numeric input
   */
  public sanitizeNumber(input: string | number): number | null {
    if (typeof input === 'number') {
      return isNaN(input) || !isFinite(input) ? null : input;
    }

    const sanitized = this.sanitizeText(input.toString()).trim();
    const num = parseFloat(sanitized);

    return isNaN(num) || !isFinite(num) ? null : num;
  }

  /**
   * Remove potentially dangerous characters from filename
   */
  public sanitizeFilename(filename: string): string {
    const sanitized = this.sanitizeText(filename);

    // Remove path traversal attempts
    const cleaned = sanitized
      .replace(/\.\./g, '')
      .replace(/[\/\\]/g, '')
      .trim();

    // Limit length
    return cleaned.substring(0, 255);
  }

  /**
   * Check if string contains potential XSS vectors
   */
  public containsXSS(input: string): boolean {
    const xssPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i, // Event handlers
      /<iframe/i,
      /<object/i,
      /<embed/i,
      /eval\(/i,
    ];

    return xssPatterns.some((pattern) => pattern.test(input));
  }

  /**
   * Strip all HTML tags
   */
  public stripHTML(html: string): string {
    return this.sanitizeHTML(html, {
      profile: SanitizationProfile.STRICT,
    }) as string;
  }
}

/**
 * Singleton sanitization service instance
 */
let sanitizationServiceInstance: SanitizationService | null = null;

/**
 * Get or create sanitization service instance
 */
export function getSanitizationService(): SanitizationService {
  if (!sanitizationServiceInstance) {
    sanitizationServiceInstance = new SanitizationService();
  }
  return sanitizationServiceInstance;
}

/**
 * Convenience functions for direct use
 */
export const sanitize = {
  html: (input: string, options?: SanitizationOptions) =>
    getSanitizationService().sanitizeHTML(input, options),
  text: (input: string) => getSanitizationService().sanitizeText(input),
  email: (input: string) => getSanitizationService().sanitizeEmail(input),
  phone: (input: string) => getSanitizationService().sanitizePhone(input),
  url: (input: string) => getSanitizationService().sanitizeURL(input),
  date: (input: string) => getSanitizationService().sanitizeDate(input),
  number: (input: string | number) => getSanitizationService().sanitizeNumber(input),
  filename: (input: string) => getSanitizationService().sanitizeFilename(input),
  object: <T extends Record<string, any>>(obj: T, profile?: SanitizationProfile) =>
    getSanitizationService().sanitizeObject(obj, profile),
};
